export type TimezoneDisplay = 'NY' | 'local' | 'UTC';

import {
  defaultChartIndicatorIds,
  normalizeChartIndicatorSettingIds,
  type ChartIndicatorSettingId,
} from './chartIndicators';

export type ChartSettings = {
  defaultTimeframe: string;
  defaultIndicatorIds: ChartIndicatorSettingId[];
  playbackSpeed: number;
  followMode: boolean;
  lockScrolling: boolean;
  showTradeMarkers: boolean;
  showTradeDrawings: boolean;
  showExecutions: boolean;
  showPnlOverlay: boolean;
  showLegend: boolean;
  hideVolumeFromCharts: boolean;
  chartType: 'candles' | 'heikin';
  timezoneDisplay: TimezoneDisplay;
};

export const CHART_SETTINGS_STORAGE_KEY = 'tradeproof:chart:settings:v1';

export const chartSettingsDefaults: ChartSettings = {
  defaultTimeframe: '5m',
  defaultIndicatorIds: defaultChartIndicatorIds,
  playbackSpeed: 1,
  followMode: true,
  lockScrolling: false,
  showTradeMarkers: true,
  showTradeDrawings: true,
  showExecutions: true,
  showPnlOverlay: true,
  showLegend: true,
  hideVolumeFromCharts: false,
  chartType: 'candles',
  timezoneDisplay: 'NY',
};

export type ChartSettingsEnvelope = {
  version: number;
  updatedAt: number;
  settings: ChartSettings;
};

export function validateAndNormalizeChartSettings(raw: unknown): ChartSettingsEnvelope {
  const now = Date.now();
  if (!raw || typeof raw !== 'object') {
    return { version: 1, updatedAt: now, settings: chartSettingsDefaults };
  }
  // raw might be either the envelope or settings object
  const maybeEnv = raw as any;
  const maybeSettings = (maybeEnv && typeof maybeEnv.settings === 'object') ? maybeEnv.settings : maybeEnv;

  const s: ChartSettings = {
    defaultTimeframe: typeof maybeSettings?.defaultTimeframe === 'string' && maybeSettings.defaultTimeframe ? String(maybeSettings.defaultTimeframe) : chartSettingsDefaults.defaultTimeframe,
    defaultIndicatorIds: Array.isArray(maybeSettings?.defaultIndicatorIds) ? normalizeChartIndicatorSettingIds(maybeSettings.defaultIndicatorIds) : chartSettingsDefaults.defaultIndicatorIds,
    playbackSpeed: Number.isFinite(Number(maybeSettings?.playbackSpeed)) ? Number(maybeSettings.playbackSpeed) : chartSettingsDefaults.playbackSpeed,
    followMode: typeof maybeSettings?.followMode === 'boolean' ? maybeSettings.followMode : chartSettingsDefaults.followMode,
    lockScrolling: typeof maybeSettings?.lockScrolling === 'boolean' ? maybeSettings.lockScrolling : chartSettingsDefaults.lockScrolling,
    showTradeMarkers: typeof maybeSettings?.showTradeMarkers === 'boolean' ? maybeSettings.showTradeMarkers : chartSettingsDefaults.showTradeMarkers,
    showTradeDrawings: typeof maybeSettings?.showTradeDrawings === 'boolean' ? maybeSettings.showTradeDrawings : chartSettingsDefaults.showTradeDrawings,
    showExecutions: typeof maybeSettings?.showExecutions === 'boolean' ? maybeSettings.showExecutions : chartSettingsDefaults.showExecutions,
    showPnlOverlay: typeof maybeSettings?.showPnlOverlay === 'boolean' ? maybeSettings.showPnlOverlay : chartSettingsDefaults.showPnlOverlay,
    showLegend: typeof maybeSettings?.showLegend === 'boolean' ? maybeSettings.showLegend : chartSettingsDefaults.showLegend,
    hideVolumeFromCharts: typeof maybeSettings?.hideVolumeFromCharts === 'boolean' ? maybeSettings.hideVolumeFromCharts : chartSettingsDefaults.hideVolumeFromCharts,
    chartType: (typeof maybeSettings?.chartType === 'string'
      ? (String(maybeSettings.chartType).toLowerCase().includes('heikin') ? 'heikin' : (String(maybeSettings.chartType).toLowerCase() === 'candles' || String(maybeSettings.chartType).toLowerCase() === 'candle' ? 'candles' : chartSettingsDefaults.chartType))
      : chartSettingsDefaults.chartType),
    timezoneDisplay: (maybeSettings?.timezoneDisplay === 'NY' || maybeSettings?.timezoneDisplay === 'local' || maybeSettings?.timezoneDisplay === 'UTC') ? maybeSettings.timezoneDisplay : chartSettingsDefaults.timezoneDisplay,
  };

  return { version: 1, updatedAt: now, settings: s };
}
